import { Component, OnInit, inject } from '@angular/core';
import { StepComponent } from '../step.component';
import { UntypedFormControl } from '@angular/forms';
import { FastModeService } from '../../../../../../services/fast-mode.service';
import { StateService } from '../../../../../../services/state.service';
import { formatTypedAmount } from './typed-amount';

@Component({
    selector: 'app-slider-step',
    templateUrl: './slider-step.component.html',
    styleUrls: ['./slider-step.component.scss'],
    standalone: false
})
export class SliderStepComponent extends StepComponent implements OnInit {
  control: UntypedFormControl;
  sliderValue: number;
  sliderValues: Array<number>;
  sliderLabels: Array<string>;
  sliderDisplay: string;
  enterHomeValue: number;
  enterStringFieldValue: string;

  private fastMode = inject(FastModeService);
  private stateService = inject(StateService);
  private fastAdvanced = false;

  /** Dollar slider steps FAST can fill from the average home value. */
  private static readonly FAST_DOLLAR_STEPS = ['cashOut', 'propertyValue', 'newHomeValue', 'firstMortgageBalance'];

  ngOnInit() {
  }

  /**
   * The typed-amount box, as Hope's (SliderStepComponent.onSliderAmountInput in ai-rate-finder, which phone keyboards handle well):
   * keep the digits, put the commas in, and write that text into the field here, inside the input event. Left to the binding,
   * the field was rewritten only after the event, under a keyboard that was still composing the number ("6400" and one more 0
   * became "6"). Returns the formatted text.
   */
  protected keepTypedAmountText(e: any): string {
    const el = e?.target;
    const prevDigits = String(this.enterStringFieldValue ?? '').replace(/\D/g, '');
    const raw = String(el?.value ?? '');
    const caret = typeof el?.selectionStart === 'number' ? el.selectionStart : raw.length;
    const typed = formatTypedAmount(raw, caret, prevDigits, e?.inputType);
    if (el && el.value !== typed.text) {
      el.value = typed.text;
      // The write sent the caret to the end: put it back beside the digit being edited.
      try {
        if (typeof el.setSelectionRange === 'function' && document.activeElement === el) { el.setSelectionRange(typed.caret, typed.caret); }
      } catch (err) { /* a field type without a caret */ }
    }
    this.enterStringFieldValue = typed.text;
    return typed.text;
  }

  /**
   * FAST mode (FRU-21170): fill the dollar fields from the average home value for
   * the visitor's IP-derived state (zip is collected later, on purpose — asking it
   * early raises drop-off). home value = avg, balance = 65%, cash out = 20%.
   * The first dollar step triggers the fetch + math; later dollar steps read their
   * override. Refinance STOPS at the cash-out step (Ed's rule); a step with no
   * override is left for the user.
   */
  fastTry(): void {
    if (!this.fastMode || !this.fastMode.canAutoAdvance || this.fastAdvanced) {
      return;
    }
    // The step's own initStep may have already answered.
    if (this.control && this.control.value !== '' && this.control.value != null) {
      return;
    }
    const name = this.fastControlName();
    if (!name) {
      return;
    }

    // Refinance: stop at the cash-out amount question. Cash-out path fills it.
    if (name === 'cashOut' && !this.fastMode.isCashout) {
      return;
    }

    const existing = this.fastMode.getAnswer(name);
    if (existing != null) {
      this.fastFill(existing);
      return;
    }

    // First dollar step: compute the dollar overrides from the IP state.
    if (SliderStepComponent.FAST_DOLLAR_STEPS.indexOf(name) >= 0 && this.fastMode.shouldComputeCashout()) {
      this.fastAdvanced = true;
      const state = this.stateService.getStateAbbreviation()
        || (this.form.controls['propertyState'] ? this.form.controls['propertyState'].value : '')
        || '';
      this.fastMode.applyDollarDefaults(state)
        .then(() => {
          const v = this.fastMode.getAnswer(name);
          if (v != null && this.fastMode.enabled) {
            this.fastApply(v);
          } else {
            this.fastAdvanced = false; // nothing to fill here -> let the user
          }
        })
        .catch(() => { this.fastAdvanced = false; }); // on error, let the user fill it
    } else if (name === 'estimatedDownPayment') {
      const hasDefaultValue = Array.isArray(this.sliderValues)
        && typeof this.sliderValue === 'number'
        && this.sliderValue >= 0
        && this.sliderValue < this.sliderValues.length
        && this.sliderValues[this.sliderValue] != null;
      if (hasDefaultValue) {
        this.fastFill(this.sliderValues[this.sliderValue]);
      }
    }
    // else: no override -> FAST stops here for the user.
  }

  private fastControlName(): string | null {
    if (!this.control || !this.form || !this.form.controls) {
      return null;
    }
    return Object.keys(this.form.controls).find(k => this.form.controls[k] === this.control) || null;
  }

  private fastFill(value: any): void {
    this.fastAdvanced = true;
    setTimeout(() => {
      if (this.fastMode.enabled) {
        this.fastApply(value);
      }
    }, 650);
  }

  private fastApply(value: any): void {
    this.fastMode.markResolved((this as any).title || 'Loan amount');
    this.control.setValue(value);
    // Derived calcs read sibling controls (cashOut, etc.) that may not exist yet;
    // never let a derive error halt the FAST advance.
    try { if (typeof this.setDesiredLoanAmount === 'function') { this.setDesiredLoanAmount(); } } catch (e) { }
    try { if (typeof (this as any).setLTV === 'function') { (this as any).setLTV(); } } catch (e) { }
    this.nextStep();
  }

  slide(e) {
    this.sliderValue = e.target.value;
    this.sliderDisplay = this.sliderLabels[e.target.value];
  }

  submit(value: any): void {
    // If no slide, use default    
    if (typeof value === 'undefined') {
      value = this.sliderValues[this.sliderValue];
    }else if(this.enterHomeValue){ 
      value = this.enterHomeValue;
    }else {
      value = this.sliderValues[value];
    }

    this.control.setValue(value);

    // Most uses for this type of step will need to update desired loan amount and LTV
    this.setDesiredLoanAmount();
    this.setLTV();

    this.analyticsService.trackQuestionAnswered();

    // Check if redirecting after above numbers are figured
    // if (this.redirectTables(value)) {
    //   this.fruHttpService.redirectRateTables(this.form);
    //   return;
    // }

    this.nextStep();
  }
}
